<template>
  <div>
    <notification-handler />
    <div v-if="!isLoaded" class="loading-app">
      <loader />
    </div>
    <router-view v-else />
  </div>
</template>

<script>
import NotificationHandler from '@/components/generic/presentational/molecules/notificationHandler/NotificationHandler.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import establishmentsService from '@/services/establishmentsService.js'
import axios from 'axios'
import { getBrandName, getBrandFolder } from '@/helpers/BrandHelper'
import { getCookie } from './helpers/cookieHelper'
import authenticationService from '@/services/authenticationService'
import dictionaryService from '@/api/server/Dictionary.js'
import warningBannerService from '@/services/warningBannerService'
import { mapGetters } from 'vuex'
import { useI18n } from 'vue-i18n'

export default {
  name: 'App',
  components: {
    NotificationHandler,
    Loader,
  },
  emits: [],
  data() {
    return {
      transitionName: null,
      isLoaded: false,
    }
  },
  computed: {
    ...mapGetters('user', ['isAuthenticated']),
    isAccessableAnonimously() {
      return authenticationService.isAccessableAnonimously()
    },
  },
  async created() {
    this.assignI18nToPlugins()
    this.applyBrandStyles()

    if (!this.isAuthenticated && !this.isAccessableAnonimously) {
      await authenticationService.login()
    } else {
      axios.defaults.baseURL = '/api/secured/carsalestool/'

      await Promise.all([
        this.initDictionaryTranslations(),
        this.initWarningBanners(),
        this.initEstablishments(),
      ])

      this.isLoaded = true
    }
  },
  methods: {
    assignI18nToPlugins() {
      window.VUE_CLI_EXPORTS.plugins.i18n = useI18n()
    },
    applyBrandStyles() {
      const fileVersion = document.querySelector('meta[name="fileversion"]').content
      ;['style', 'fonts/FontAwesome/all.min'].forEach((style) => {
        let link = document.createElement('link')
        link.setAttribute('rel', 'stylesheet')
        link.setAttribute('href', `/wwwroot/${style}.css?v=${fileVersion}`)

        document.head.appendChild(link)
      })
      const brandName = getBrandName()
      document.body.setAttribute('class', `theme-${brandName}`)

      if (getCookie('use-legacy-style')) {
        document.body.classList.add(`theme-${brandName}-fix`)
      }

      document.querySelectorAll('link.favicon').forEach((favicon) => {
        favicon.href = favicon.href.replace('/favicons/', `/favicons/${getBrandFolder()}/`)
      })
    },
    async initDictionaryTranslations() {
      await dictionaryService.initDictionaryTranslations()
      this.$store.commit('dictionary/setDictionaryLoaded')
    },
    async initWarningBanners() {
      await warningBannerService.getActiveWarningBanners()
      //refresh pcqm dashboard warning every 5 minutes
      setInterval(() => warningBannerService.getActiveWarningBanners(), 5 * 60 * 1000)
    },
    async initEstablishments() {
      if (!this.isAuthenticated) return

      await establishmentsService.getEstablishments()
      // The initial navigation is still pending here, so $route is not populated yet.
      await this.$router.isReady()
      await this.redirectWhenEstablishmentIsUnknown()
    },
    async redirectWhenEstablishmentIsUnknown() {
      const establishmentId = this.$route.params?.establishmentId
      if (!establishmentId) return

      if (this.$store.getters['establishments/establishmentById'](establishmentId)) return

      await this.$router.replace({ name: 'establishment-selection-short' })
    },
  },
}
</script>

<style lang="scss" scoped>
.loading-app {
  display: flex;
  align-content: center;
  align-items: center;
  height: 100vh;
}
</style>
@/api/server/WarningBanner.api.js
